// src/components/CheckoutBatchPrintModal.jsx
//
// SuperAdmin tool: generate one large multi-page PDF containing every
// visible student's checkout sheet, in alphabetical order, ready to
// duplex-print.
//
// Options:
//   - Duplex (force every student to even page count with a "intentionally
//     blank" spacer when needed). Default ON.
//   - Black & white (grayscale palette + visual fallbacks for status pills).
//     Default ON to save color toner for year-end printing.
//
// Renders a progress bar while generating since 400+ records can take
// ~30-60 seconds in the browser.

import React, { useState } from 'react';
import { generateBatchCheckoutPdf } from '../utils/checkoutPdf';
import { KA_COLORS } from '../utils/checkoutHelpers';

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: 6,
    width: 'min(540px, 92vw)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: {
    padding: '16px 20px',
    background: KA_COLORS.navy,
    color: '#fff',
  },
  headerTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  headerSub: {
    fontSize: 12,
    margin: '4px 0 0',
    opacity: 0.85,
  },
  body: {
    padding: '20px',
    color: '#222',
    fontSize: 14,
    lineHeight: 1.5,
  },
  count: {
    fontSize: 14,
    color: '#444',
    marginBottom: 14,
  },
  optionRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '10px 0',
    borderTop: '1px solid #eee',
  },
  optionRowFirst: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '10px 0',
  },
  checkbox: {
    marginTop: 2,
    width: 16,
    height: 16,
    cursor: 'pointer',
  },
  optionLabel: {
    flex: 1,
    cursor: 'pointer',
  },
  optionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: KA_COLORS.navy,
  },
  optionDesc: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  progressWrap: {
    marginTop: 18,
    background: '#F5F5F5',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    padding: '12px 14px',
  },
  progressLabel: {
    fontSize: 13,
    color: KA_COLORS.navy,
    marginBottom: 8,
  },
  progressBar: {
    height: 6,
    background: '#E0E0E0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: (pct) => ({
    width: `${pct}%`,
    height: '100%',
    background: KA_COLORS.gold,
    transition: 'width 0.15s ease',
  }),
  progressMeta: {
    fontSize: 11,
    color: '#666',
    marginTop: 6,
    fontStyle: 'italic',
  },
  errorBox: {
    marginTop: 16,
    padding: '10px 12px',
    background: '#FEEBE9',
    border: `1px solid ${KA_COLORS.red}`,
    borderRadius: 4,
    color: KA_COLORS.red,
    fontSize: 13,
  },
  footer: {
    padding: '14px 20px',
    background: '#FAFAFA',
    borderTop: '1px solid #E0E0E0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  btn: (variant) => {
    const palette = {
      primary: { bg: KA_COLORS.red, fg: '#fff', border: KA_COLORS.red },
      secondary: { bg: '#fff', fg: KA_COLORS.navy, border: '#CCC' },
    };
    const p = palette[variant] || palette.secondary;
    return {
      padding: '8px 18px',
      border: `1px solid ${p.border}`,
      background: p.bg,
      color: p.fg,
      borderRadius: 4,
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 600,
    };
  },
};

const CheckoutBatchPrintModal = ({ records, onClose }) => {
  const [duplex, setDuplex] = useState(true);
  const [grayscale, setGrayscale] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [error, setError] = useState(null);

  const total = records ? records.length : 0;

  const handleGenerate = async () => {
    if (!total || generating) return;
    setError(null);
    setGenerating(true);
    setProgress({ done: 0, total, current: '' });

    try {
      await generateBatchCheckoutPdf(records, {
        colorMode: grayscale ? 'grayscale' : 'color',
        duplex,
        onProgress: (done, totalCount, name) => {
          setProgress({ done, total: totalCount, current: name });
        },
      });
      // Auto-close on success after a short pause so the user sees 100%.
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('generateBatchCheckoutPdf failed:', err);
      setError(err?.message || 'Failed to generate the PDF. Please try again.');
      setGenerating(false);
    }
  };

  const pct = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div style={styles.backdrop} onClick={generating ? undefined : onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Batch Print Checkout PDFs</h2>
          <p style={styles.headerSub}>
            Generates one PDF with every visible student's checkout sheet,
            sorted alphabetically.
          </p>
        </div>

        <div style={styles.body}>
          <div style={styles.count}>
            <strong>{total}</strong> student{total === 1 ? '' : 's'} will be
            included.
          </div>

          <div style={styles.optionRowFirst}>
            <input
              type="checkbox"
              id="batch-duplex"
              style={styles.checkbox}
              checked={duplex}
              disabled={generating}
              onChange={(e) => setDuplex(e.target.checked)}
            />
            <label htmlFor="batch-duplex" style={styles.optionLabel}>
              <div style={styles.optionTitle}>Force duplex (recommended)</div>
              <div style={styles.optionDesc}>
                Pads each student's sheet to an even number of pages with a
                blank spacer when needed, so duplex printing keeps each
                student on their own pair of pages.
              </div>
            </label>
          </div>

          <div style={styles.optionRow}>
            <input
              type="checkbox"
              id="batch-grayscale"
              style={styles.checkbox}
              checked={grayscale}
              disabled={generating}
              onChange={(e) => setGrayscale(e.target.checked)}
            />
            <label htmlFor="batch-grayscale" style={styles.optionLabel}>
              <div style={styles.optionTitle}>
                Black &amp; white (saves color toner)
              </div>
              <div style={styles.optionDesc}>
                Renders the PDF in grayscale with bold text and ✓ marks
                replacing color cues on status pills. Recommended for
                year-end batch printing.
              </div>
            </label>
          </div>

          {generating && (
            <div style={styles.progressWrap}>
              <div style={styles.progressLabel}>
                Generating: <strong>{progress.done}</strong> of{' '}
                <strong>{progress.total}</strong> ({pct}%)
              </div>
              <div style={styles.progressBar}>
                <div style={styles.progressFill(pct)} />
              </div>
              {progress.current && (
                <div style={styles.progressMeta}>
                  Currently rendering: {progress.current}
                </div>
              )}
            </div>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.btn('secondary')}
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            style={styles.btn('primary')}
            onClick={handleGenerate}
            disabled={generating || total === 0}
          >
            {generating ? 'Generating…' : `Generate PDF for ${total}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutBatchPrintModal;